import { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { Modal, Button } from '@shared/components';
import type { Quote, Customer, Employee } from '@shared/types';
import type { QuoteCreateInput } from '../services/quote.service';
import { fetchCustomers } from '@modules/customer';
import { useEmployee } from '@modules/master/hooks/useEmployee';

const DEFAULT: QuoteCreateInput = {
  customerId: '', contactId: null, title: '',
  validUntil: Timestamp.now(), notes: null, internalNotes: null,
  assignedUserId: '', templateId: null,
  orderId: null, usergroupId: null, businessTypeId: null,
  taxCalcMode: 'floor', advance: 0, totalgst: 0, grandtotal: 0,
  disabled: false, code: null, samemaster: false,
  chiefEmployerId: null, atenaType: 'company_honorific', paymentTerm: '',
  stampFlags: { tantou: false, kakunin: false, kaisha: true },
};

function quoteToInput(q: Quote): QuoteCreateInput {
  return {
    customerId: q.customerId, contactId: q.contactId, title: q.title,
    validUntil: q.validUntil, notes: q.notes, internalNotes: q.internalNotes,
    assignedUserId: q.assignedUserId, templateId: q.templateId,
    orderId: q.orderId ?? null, usergroupId: q.usergroupId ?? null, businessTypeId: q.businessTypeId ?? null,
    taxCalcMode: q.taxCalcMode ?? 'floor',
    advance: q.advance ?? 0, totalgst: q.totalgst ?? 0, grandtotal: q.grandtotal ?? 0,
    disabled: q.disabled ?? false, code: q.code ?? null,
    samemaster: q.samemaster ?? false, chiefEmployerId: q.chiefEmployerId ?? null,
    atenaType: q.atenaType ?? 'company_honorific', paymentTerm: q.paymentTerm ?? '',
    stampFlags: q.stampFlags ?? { tantou: false, kakunin: false, kaisha: true },
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  quote?: Quote | null;
  initial?: Partial<QuoteCreateInput>;
  onSubmit: (data: QuoteCreateInput) => Promise<void>;
}

const cls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

export function QuoteFormModal({ isOpen, onClose, quote, initial, onSubmit }: Props) {
  const [form, setForm] = useState<QuoteCreateInput>(quote ? quoteToInput(quote) : { ...DEFAULT, ...initial });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { employees, loadEmployees } = useEmployee();
  const upd = (patch: Partial<QuoteCreateInput>) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    void fetchCustomers()
      .then(setCustomers)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : '顧客リストの取得に失敗しました');
      });
    void loadEmployees();
  }, [loadEmployees]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null); setSaving(true);
    try { await onSubmit(form); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={quote ? '見積編集' : '見積作成'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <QuoteBasicSection form={form} upd={upd} customers={customers} employees={employees} cls={cls} />
        <QuoteDocSection form={form} upd={upd} employees={employees} cls={cls} />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface BasicProps {
  form: QuoteCreateInput;
  upd: (p: Partial<QuoteCreateInput>) => void;
  customers: Customer[];
  employees: Employee[];
  cls: string;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function QuoteBasicSection({ form, upd, customers, employees, cls }: BasicProps) {
  const dateStr = form.validUntil.toDate().toISOString().split('T')[0];
  return (
    <div className="space-y-4">
      <Field label="件名 *">
        <input value={form.title} onChange={(e) => upd({ title: e.target.value })} required className={cls} />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="顧客 *">
          <select value={form.customerId} onChange={(e) => upd({ customerId: e.target.value })} required className={cls}>
            <option value="">選択してください</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="担当者 *">
          <select value={form.assignedUserId} onChange={(e) => upd({ assignedUserId: e.target.value })} required className={cls}>
            <option value="">選択してください</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="有効期限 *">
        <input type="date" value={dateStr} required
          onChange={(e) => upd({ validUntil: Timestamp.fromDate(new Date(e.target.value)) })} className={cls} />
      </Field>
      <Field label="備考">
        <textarea value={form.notes ?? ''} onChange={(e) => upd({ notes: e.target.value || null })}
          rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <Field label="社内メモ">
        <textarea value={form.internalNotes ?? ''} onChange={(e) => upd({ internalNotes: e.target.value || null })}
          rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      </Field>
    </div>
  );
}

interface DocProps {
  form: QuoteCreateInput;
  upd: (p: Partial<QuoteCreateInput>) => void;
  employees: Employee[];
  cls: string;
}

function QuoteDocSection({ form, upd, employees, cls }: DocProps) {
  return (
    <details className="border border-gray-200 rounded-lg" open>
      <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 select-none">
        帳票設定
      </summary>
      <div className="px-4 pb-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="税計算モード">
            <select value={form.taxCalcMode} onChange={(e) => upd({ taxCalcMode: e.target.value as QuoteCreateInput['taxCalcMode'] })} className={cls}>
              <option value="floor">切捨</option>
              <option value="ceil">切上</option>
              <option value="round">四捨五入</option>
              <option value="manual">手動</option>
            </select>
          </Field>
          <Field label="宛名種別">
            <select value={form.atenaType} onChange={(e) => upd({ atenaType: e.target.value as QuoteCreateInput['atenaType'] })} className={cls}>
              <option value="company_honorific">会社+御中</option>
              <option value="dept_honorific">部署+御中</option>
              <option value="contact_sama">担当者+様</option>
            </select>
          </Field>
        </div>
        <Field label="支払条件">
          <input value={form.paymentTerm} onChange={(e) => upd({ paymentTerm: e.target.value })}
            placeholder="例：月末締翌月末払い" className={cls} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="立替金額（非課税）">
            <input type="number" min={0} value={form.advance}
              onChange={(e) => upd({ advance: Number(e.target.value) })} className={cls} />
          </Field>
          <Field label="確認者">
            <select value={form.chiefEmployerId ?? ''} onChange={(e) => upd({ chiefEmployerId: e.target.value || null })} className={cls}>
              <option value="">なし</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
          </Field>
        </div>
        <StampFlagsField flags={form.stampFlags} onUpdate={(flags) => upd({ stampFlags: flags })} />
      </div>
    </details>
  );
}

interface StampFlagsProps {
  flags: QuoteCreateInput['stampFlags'];
  onUpdate: (flags: QuoteCreateInput['stampFlags']) => void;
}

function StampFlagsField({ flags, onUpdate }: StampFlagsProps) {
  const toggle = (key: keyof typeof flags) => onUpdate({ ...flags, [key]: !flags[key] });
  return (
    <fieldset>
      <legend className="text-sm font-medium text-gray-700 mb-2">捺印フラグ</legend>
      <div className="flex gap-6">
        {([['tantou', '担当者印'], ['kakunin', '確認者印'], ['kaisha', '会社印']] as const).map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={flags[k]} onChange={() => toggle(k)}
              className="rounded border-gray-300 text-blue-600" />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
